import type { DashboardOverviewDto } from '../../types/generated/models/dashboard-overview-dto'
import type { DashboardOverviewDtoApiResponse } from '../../types/generated/models/dashboard-overview-dto-api-response'
import type { OrganisationListItemDto } from '../../types/generated/models/organisation-list-item-dto'
import type { OrganisationListItemDtoPaginatedApiResponse } from '../../types/generated/models/organisation-list-item-dto-paginated-api-response'
import type { SubscriptionListItemDto } from '../../types/generated/models/subscription-list-item-dto'
import type { SubscriptionListItemDtoPaginatedApiResponse } from '../../types/generated/models/subscription-list-item-dto-paginated-api-response'
import type { SubscriptionDetailDto } from '../../types/generated/models/subscription-detail-dto'
import type { SubscriptionDetailDtoApiResponse } from '../../types/generated/models/subscription-detail-dto-api-response'
import type { JobLogListItemDto } from '../../types/generated/models/job-log-list-item-dto'
import type { JobLogListItemDtoPaginatedApiResponse } from '../../types/generated/models/job-log-list-item-dto-paginated-api-response'
import type { JobLogDetailDto } from '../../types/generated/models/job-log-detail-dto'
import type { JobLogDetailDtoApiResponse } from '../../types/generated/models/job-log-detail-dto-api-response'
import type { PaginationInfo } from '../../types/generated/models/pagination-info'

/* ── State interface ── */

export interface DashboardState {
  overview: DashboardOverviewDto
  organisations: {
    data: OrganisationListItemDto[]
    pagination: PaginationInfo
  }
  subscriptions: {
    data: SubscriptionListItemDto[]
    pagination: PaginationInfo
  }
  subscriptionDetails: SubscriptionDetailDto | null
  plusFeaturesConfig: {
    availableFeatures: any[]
    enabledFeatures: any[]
    loading: boolean
    saving: boolean
    error: boolean | null
    errorMessage: string | null
    successMessage: string | null
  }
  jobLogs: {
    data: JobLogListItemDto[]
    pagination: PaginationInfo
    loading: boolean
    error: boolean | null
    errorMessage: string | null
  }
  jobLogDetails: {
    data: JobLogDetailDto | null
    loading: boolean
    error: boolean | null
    errorMessage: string | null
  }
  loading: boolean
  error: boolean | null
  errorMessage: string | null
}

/* ── Action types ── */

export const GET_DASHBOARD_OVERVIEW = 'GET_DASHBOARD_OVERVIEW'
export const GET_DASHBOARD_OVERVIEW_SUCCESS = 'GET_DASHBOARD_OVERVIEW_SUCCESS'
export const GET_DASHBOARD_OVERVIEW_FAIL = 'GET_DASHBOARD_OVERVIEW_FAIL'

export const GET_DASHBOARD_ORGANISATIONS = 'GET_DASHBOARD_ORGANISATIONS'
export const GET_DASHBOARD_ORGANISATIONS_SUCCESS = 'GET_DASHBOARD_ORGANISATIONS_SUCCESS'
export const GET_DASHBOARD_ORGANISATIONS_FAIL = 'GET_DASHBOARD_ORGANISATIONS_FAIL'

export const GET_DASHBOARD_SUBSCRIPTIONS = 'GET_DASHBOARD_SUBSCRIPTIONS'
export const GET_DASHBOARD_SUBSCRIPTIONS_SUCCESS = 'GET_DASHBOARD_SUBSCRIPTIONS_SUCCESS'
export const GET_DASHBOARD_SUBSCRIPTIONS_FAIL = 'GET_DASHBOARD_SUBSCRIPTIONS_FAIL'

export const GET_SUBSCRIPTION_DETAILS = 'GET_SUBSCRIPTION_DETAILS'
export const GET_SUBSCRIPTION_DETAILS_SUCCESS = 'GET_SUBSCRIPTION_DETAILS_SUCCESS'
export const GET_SUBSCRIPTION_DETAILS_FAIL = 'GET_SUBSCRIPTION_DETAILS_FAIL'

// Plus Features Config
export const GET_PLUS_FEATURES_CONFIG = 'GET_PLUS_FEATURES_CONFIG'
export const GET_PLUS_FEATURES_CONFIG_SUCCESS = 'GET_PLUS_FEATURES_CONFIG_SUCCESS'
export const GET_PLUS_FEATURES_CONFIG_FAIL = 'GET_PLUS_FEATURES_CONFIG_FAIL'

export const UPDATE_PLUS_FEATURES_CONFIG = 'UPDATE_PLUS_FEATURES_CONFIG'
export const UPDATE_PLUS_FEATURES_CONFIG_SUCCESS = 'UPDATE_PLUS_FEATURES_CONFIG_SUCCESS'
export const UPDATE_PLUS_FEATURES_CONFIG_FAIL = 'UPDATE_PLUS_FEATURES_CONFIG_FAIL'

// Background Job Logs
export const GET_JOB_LOGS = 'GET_JOB_LOGS'
export const GET_JOB_LOGS_SUCCESS = 'GET_JOB_LOGS_SUCCESS'
export const GET_JOB_LOGS_FAIL = 'GET_JOB_LOGS_FAIL'

export const GET_JOB_LOG_DETAILS = 'GET_JOB_LOG_DETAILS'
export const GET_JOB_LOG_DETAILS_SUCCESS = 'GET_JOB_LOG_DETAILS_SUCCESS'
export const GET_JOB_LOG_DETAILS_FAIL = 'GET_JOB_LOG_DETAILS_FAIL'

/* ── Initial state ── */

export const defaultDashboardState: DashboardState = {
  overview: {
    totalOrganisations: 0,
    totalSubscriptions: 0,
    activeSubscriptions: 0,
    pastDueSubscriptions: 0,
    totalDevices: 0,
    totalMonthlyRevenueInPence: 0,
    totalMonthlyRevenueGBP: 0
  },
  organisations: {
    data: [],
    pagination: {
      pageNumber: 1,
      pageSize: 10,
      totalCount: 0,
      totalPages: 0
    }
  },
  subscriptions: {
    data: [],
    pagination: {
      pageNumber: 1,
      pageSize: 10,
      totalCount: 0,
      totalPages: 0
    }
  },
  subscriptionDetails: null,
  // Plus Features Config state
  plusFeaturesConfig: {
    availableFeatures: [],
    enabledFeatures: [],
    loading: false,
    saving: false,
    error: null,
    errorMessage: null,
    successMessage: null
  },
  // Background Job Logs state
  jobLogs: {
    data: [],
    pagination: {
      pageNumber: 1,
      pageSize: 20,
      totalCount: 0,
      totalPages: 0
    },
    loading: false,
    error: null,
    errorMessage: null
  },
  jobLogDetails: {
    data: null,
    loading: false,
    error: null,
    errorMessage: null
  },
  loading: false,
  error: null,
  errorMessage: null
}

/* ── Reducer ── */

export default (state: DashboardState = defaultDashboardState, action: any): DashboardState => {
  switch (action.type) {
    case GET_DASHBOARD_OVERVIEW:
      return {
        ...state,
        loading: true,
        error: null
      }
    
    case GET_DASHBOARD_OVERVIEW_SUCCESS: {
      const response = action.payload.data as DashboardOverviewDtoApiResponse
      if (response.success) {
        return {
          ...state,
          overview: response.data as DashboardOverviewDto,
          loading: false,
          error: false,
          errorMessage: null
        }
      }
      return {
        ...state,
        loading: false,
        error: true,
        errorMessage: response.error ?? null
      }
    }
    
    case GET_DASHBOARD_OVERVIEW_FAIL:
      return {
        ...state,
        loading: false,
        error: true,
        errorMessage: action.error?.message || 'Failed to load overview'
      }

    case GET_DASHBOARD_ORGANISATIONS:
      return {
        ...state,
        loading: true,
        error: null
      }
    
    case GET_DASHBOARD_ORGANISATIONS_SUCCESS: {
      const response = action.payload.data as OrganisationListItemDtoPaginatedApiResponse
      if (response.success) {
        return {
          ...state,
          organisations: {
            data: (response.data ?? []) as OrganisationListItemDto[],
            pagination: (response.pagination ?? defaultDashboardState.organisations.pagination) as PaginationInfo
          },
          loading: false,
          error: false,
          errorMessage: null
        }
      }
      return {
        ...state,
        loading: false,
        error: true,
        errorMessage: response.error ?? null
      }
    }
    
    case GET_DASHBOARD_ORGANISATIONS_FAIL:
      return {
        ...state,
        loading: false,
        error: true,
        errorMessage: action.error?.message || 'Failed to load organisations'
      }

    case GET_DASHBOARD_SUBSCRIPTIONS:
      return {
        ...state,
        loading: true,
        error: null
      }
    
    case GET_DASHBOARD_SUBSCRIPTIONS_SUCCESS: {
      const response = action.payload.data as SubscriptionListItemDtoPaginatedApiResponse
      if (response.success) {
        return {
          ...state,
          subscriptions: {
            data: (response.data ?? []) as SubscriptionListItemDto[],
            pagination: (response.pagination ?? defaultDashboardState.subscriptions.pagination) as PaginationInfo
          },
          loading: false,
          error: false,
          errorMessage: null
        }
      }
      return {
        ...state,
        loading: false,
        error: true,
        errorMessage: response.error ?? null
      }
    }
    
    case GET_DASHBOARD_SUBSCRIPTIONS_FAIL:
      return {
        ...state,
        loading: false,
        error: true,
        errorMessage: action.error?.message || 'Failed to load subscriptions'
      }

    case GET_SUBSCRIPTION_DETAILS:
      return {
        ...state,
        loading: true,
        error: null
      }
    
    case GET_SUBSCRIPTION_DETAILS_SUCCESS: {
      const response = action.payload.data as SubscriptionDetailDtoApiResponse
      if (response.success) {
        return {
          ...state,
          subscriptionDetails: (response.data ?? null) as SubscriptionDetailDto | null,
          loading: false,
          error: false,
          errorMessage: null
        }
      }
      return {
        ...state,
        loading: false,
        error: true,
        errorMessage: response.error ?? null
      }
    }
    
    case GET_SUBSCRIPTION_DETAILS_FAIL:
      return {
        ...state,
        loading: false,
        error: true,
        errorMessage: action.error?.message || 'Failed to load subscription details'
      }

    // Plus Features Config reducers
    case GET_PLUS_FEATURES_CONFIG:
      return {
        ...state,
        plusFeaturesConfig: {
          ...state.plusFeaturesConfig,
          loading: true,
          error: null,
          errorMessage: null,
          successMessage: null  // Clear success message when reloading
        }
      }
    
    case GET_PLUS_FEATURES_CONFIG_SUCCESS:
      if (action.payload.data.success) {
        return {
          ...state,
          plusFeaturesConfig: {
            ...state.plusFeaturesConfig,
            availableFeatures: action.payload.data.data.availableFeatures,
            enabledFeatures: action.payload.data.data.enabledFeatures,
            loading: false,
            error: null,
            errorMessage: null
          }
        }
      }
      return {
        ...state,
        plusFeaturesConfig: {
          ...state.plusFeaturesConfig,
          loading: false,
          error: true,
          errorMessage: action.payload.data.error || 'Failed to load configuration'
        }
      }
    
    case GET_PLUS_FEATURES_CONFIG_FAIL:
      return {
        ...state,
        plusFeaturesConfig: {
          ...state.plusFeaturesConfig,
          loading: false,
          error: true,
          errorMessage: action.error?.message || 'Failed to load configuration'
        }
      }
    
    case UPDATE_PLUS_FEATURES_CONFIG:
      return {
        ...state,
        plusFeaturesConfig: {
          ...state.plusFeaturesConfig,
          saving: true,
          error: null,
          errorMessage: null,
          successMessage: null
        }
      }
    
    case UPDATE_PLUS_FEATURES_CONFIG_SUCCESS:
      if (action.payload.data.success) {
        return {
          ...state,
          plusFeaturesConfig: {
            ...state.plusFeaturesConfig,
            enabledFeatures: action.payload.data.data.enabledFeatures,
            saving: false,
            error: null,
            errorMessage: null,
            successMessage: 'Configuration saved successfully!'
          }
        }
      }
      return {
        ...state,
        plusFeaturesConfig: {
          ...state.plusFeaturesConfig,
          saving: false,
          error: true,
          errorMessage: action.payload.data.error || 'Failed to save configuration'
        }
      }
    
    case UPDATE_PLUS_FEATURES_CONFIG_FAIL:
      return {
        ...state,
        plusFeaturesConfig: {
          ...state.plusFeaturesConfig,
          saving: false,
          error: true,
          errorMessage: action.error?.message || 'Failed to save configuration'
        }
      }

    // Background Job Logs
    case GET_JOB_LOGS:
      return {
        ...state,
        jobLogs: {
          ...state.jobLogs,
          loading: true,
          error: null,
          errorMessage: null
        }
      }
    
    case GET_JOB_LOGS_SUCCESS: {
      const response = action.payload.data as JobLogListItemDtoPaginatedApiResponse
      if (response.success) {
        return {
          ...state,
          jobLogs: {
            data: (response.data ?? []) as JobLogListItemDto[],
            pagination: (response.pagination ?? defaultDashboardState.jobLogs.pagination) as PaginationInfo,
            loading: false,
            error: null,
            errorMessage: null
          }
        }
      }
      return {
        ...state,
        jobLogs: {
          ...state.jobLogs,
          loading: false,
          error: true,
          errorMessage: response.error ?? null
        }
      }
    }
    
    case GET_JOB_LOGS_FAIL:
      return {
        ...state,
        jobLogs: {
          ...state.jobLogs,
          loading: false,
          error: true,
          errorMessage: action.error?.message || 'Failed to load job logs'
        }
      }

    case GET_JOB_LOG_DETAILS:
      return {
        ...state,
        jobLogDetails: {
          ...state.jobLogDetails,
          loading: true,
          error: null,
          errorMessage: null
        }
      }
    
    case GET_JOB_LOG_DETAILS_SUCCESS: {
      const response = action.payload.data as JobLogDetailDtoApiResponse
      if (response.success) {
        return {
          ...state,
          jobLogDetails: {
            data: (response.data ?? null) as JobLogDetailDto | null,
            loading: false,
            error: null,
            errorMessage: null
          }
        }
      }
      return {
        ...state,
        jobLogDetails: {
          ...state.jobLogDetails,
          loading: false,
          error: true,
          errorMessage: response.error ?? null
        }
      }
    }
    
    case GET_JOB_LOG_DETAILS_FAIL:
      return {
        ...state,
        jobLogDetails: {
          ...state.jobLogDetails,
          loading: false,
          error: true,
          errorMessage: action.error?.message || 'Failed to load job log details'
        }
      }

    default:
      return state
  }
}
